import { useState } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    TextField,
    Checkbox,
    Button,
    Radio
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { QUESTION_TYPE_KEYS } from "../../enums/QuestionTypes";

const FormPreviewDialog = ({ open, onClose, form }) => {
    const [yesNoAnswers, setYesNoAnswers] = useState({});

    if (!form) return null;

    const handleYesNoClick = (questionId, answer) => {
        setYesNoAnswers(prev => ({
            ...prev,
            [questionId]: prev[questionId] === answer ? null : answer
        }));
    };

    const renderQuestionPreview = (question, index) => {
        switch (question.type) {
            case QUESTION_TYPE_KEYS.FORM_TITLE:
                return (
                    <Box sx={{ textAlign: "center", padding: "24px 0" }}>
                        <Typography
                            sx={{
                                fontSize: "28px",
                                fontWeight: 600,
                                color: "#1E293B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.title || 'כותרת הטופס'}
                        </Typography>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.SECTION_HEADER:
                return (
                    <Box sx={{ padding: "16px 0" }}>
                        <Typography
                            sx={{
                                fontSize: "20px",
                                fontWeight: 600,
                                color: "#1E293B",
                                borderBottom: "2px solid #57837E",
                                paddingBottom: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.title || 'כותרת מקטע'}
                        </Typography>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.FREE_TEXT:
                return (
                    <Box
                        sx={{
                            backgroundColor: "#E8F5F3",
                            borderRight: "4px solid #57837E",
                            padding: "16px",
                            borderRadius: "0 8px 8px 0"
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#1E293B",
                                whiteSpace: "pre-wrap",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.content || 'טקסט חופשי'}
                        </Typography>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.YES_NO:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <Box sx={{ display: "flex", gap: "12px" }}>
                            <Button
                                variant={yesNoAnswers[question.id] === 'yes' ? "contained" : "outlined"}
                                onClick={() => handleYesNoClick(question.id, 'yes')}
                                sx={{
                                    backgroundColor: yesNoAnswers[question.id] === 'yes' ? "#57837E" : "transparent",
                                    borderColor: yesNoAnswers[question.id] === 'yes' ? "#57837E" : "#E2E8F0",
                                    color: yesNoAnswers[question.id] === 'yes' ? "#FFFFFF" : "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    "&:hover": {
                                        backgroundColor: yesNoAnswers[question.id] === 'yes' ? "#46706B" : "#F8FAFC",
                                        borderColor: "#57837E"
                                    }
                                }}
                            >
                                כן
                            </Button>
                            <Button
                                variant={yesNoAnswers[question.id] === 'no' ? "contained" : "outlined"}
                                onClick={() => handleYesNoClick(question.id, 'no')}
                                sx={{
                                    backgroundColor: yesNoAnswers[question.id] === 'no' ? "#57837E" : "transparent",
                                    borderColor: yesNoAnswers[question.id] === 'no' ? "#57837E" : "#E2E8F0",
                                    color: yesNoAnswers[question.id] === 'no' ? "#FFFFFF" : "#1E293B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    "&:hover": {
                                        backgroundColor: yesNoAnswers[question.id] === 'no' ? "#46706B" : "#F8FAFC",
                                        borderColor: "#57837E"
                                    }
                                }}
                            >
                                לא
                            </Button>
                        </Box>
                        {!!question.showDetailsOnYes && yesNoAnswers[question.id] === 'yes' && (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                placeholder={question.detailsPlaceholder || "פירוט (אופציונלי)"}
                                sx={{
                                    marginTop: "12px",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }
                                }}
                            />
                        )}
                        {!!question.showDetailsOnNo && yesNoAnswers[question.id] === 'no' && (
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                placeholder={question.detailsPlaceholderNo || "פירוט (אופציונלי)"}
                                sx={{
                                    marginTop: "12px",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        fontFamily: '"Heebo", "Roboto", sans-serif'
                                    }
                                }}
                            />
                        )}
                    </Box>
                );

            case QUESTION_TYPE_KEYS.SHORT_TEXT:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder={question.placeholder || 'תשובה קצרה...'}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPE_KEYS.LONG_TEXT:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder={question.placeholder || 'תשובה מפורטת...'}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPE_KEYS.MULTIPLE_CHOICE:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {question.options?.map((option, optionIndex) => (
                                <Box
                                    key={optionIndex}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        padding: "12px",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "8px",
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "#F8FAFC"
                                        }
                                    }}
                                >
                                    {question.allowMultiple ? (
                                        <Checkbox size="small" sx={{ padding: 0, color: "#57837E", "&.Mui-checked": { color: "#57837E" } }} />
                                    ) : (
                                        <Radio size="small" sx={{ padding: 0, color: "#57837E", "&.Mui-checked": { color: "#57837E" } }} />
                                    )}
                                    <Typography sx={{ fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                        {option}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.NUMBER:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <TextField
                            type="number"
                            placeholder="0"
                            sx={{
                                width: "200px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPE_KEYS.DATE:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <TextField
                            type="date"
                            sx={{
                                width: "200px",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPE_KEYS.FILE_UPLOAD:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <Box
                            sx={{
                                border: "2px dashed #E2E8F0",
                                borderRadius: "8px",
                                padding: "32px",
                                textAlign: "center",
                                cursor: "pointer",
                                "&:hover": {
                                    borderColor: "#57837E",
                                    backgroundColor: "#F8FAFC"
                                }
                            }}
                        >
                            <Typography sx={{ color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                לחץ או גרור קובץ לכאן
                            </Typography>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.PDF_ATTACHMENT:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: "#F8FAFC",
                                border: "1px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                📄 קובץ PDF מצורף
                            </Typography>
                            <Button
                                variant="text"
                                sx={{
                                    color: "#57837E",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                הורד / צפה
                            </Button>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.SIGNATURE:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <Box
                            sx={{
                                border: "2px solid #E2E8F0",
                                borderRadius: "8px",
                                padding: "16px",
                                height: "150px",
                                backgroundColor: "#FAFAFA",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <Typography sx={{ color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                חתום כאן
                            </Typography>
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.CONSENT_CHECKBOX:
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "16px",
                            border: "2px solid #E2E8F0",
                            borderRadius: "8px",
                            cursor: "pointer",
                            "&:hover": {
                                backgroundColor: "#F8FAFC"
                            }
                        }}
                    >
                        <Checkbox sx={{ padding: 0, color: "#57837E", "&.Mui-checked": { color: "#57837E" } }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.EMAIL:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <TextField
                            fullWidth
                            type="email"
                            placeholder={question.placeholder || 'example@email.com'}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPE_KEYS.PHONE:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <TextField
                            fullWidth
                            type="tel"
                            placeholder={question.placeholder || '050-0000000'}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }
                            }}
                        />
                    </Box>
                );

            case QUESTION_TYPE_KEYS.RATING:
                return (
                    <Box>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: "15px",
                                color: "#1E293B",
                                marginBottom: "12px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {question.question}
                            {question.required && <span style={{ color: "#EF4444", marginRight: "4px" }}>*</span>}
                        </Typography>
                        <Box sx={{ display: "flex", gap: "8px" }}>
                            {/* Star scale is capped at 5 — clamp legacy data that may carry more. */}
                            {[...Array(Math.min(question.maxRating || 5, 5))].map((_, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: "40px",
                                        height: "40px",
                                        border: "1px solid #E2E8F0",
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        fontSize: "20px",
                                        "&:hover": {
                                            backgroundColor: "#FEF3C7",
                                            borderColor: "#F59E0B"
                                        }
                                    }}
                                >
                                    ⭐
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );

            case QUESTION_TYPE_KEYS.LINK:
                return (
                    <Box
                        sx={{
                            backgroundColor: "#E8F5F3",
                            border: "1px solid #B9D9D5",
                            borderRadius: "8px",
                            padding: "16px"
                        }}
                    >
                        <Typography
                            component="a"
                            href={question.linkUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: "#57837E",
                                fontWeight: 600,
                                textDecoration: "none",
                                fontSize: "15px",
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                "&:hover": {
                                    textDecoration: "underline"
                                }
                            }}
                        >
                            {question.question}
                            <span style={{ fontSize: "12px" }}>↗</span>
                        </Typography>
                        {question.linkDescription && (
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    color: "#64748B",
                                    marginTop: "8px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                {question.linkDescription}
                            </Typography>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    maxHeight: "90vh"
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 24px",
                    borderBottom: "1px solid #E2E8F0",
                    direction: "rtl"
                }}
            >
                <Typography
                    sx={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#1E293B",
                        fontFamily: '"Heebo", "Roboto", sans-serif'
                    }}
                >
                    תצוגה מקדימה
                </Typography>
                <IconButton onClick={onClose} sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent sx={{ padding: "24px", direction: "rtl" }}>
                <Box sx={{ maxWidth: "600px", margin: "0 auto" }}>
                    {/* Logo */}
                    {form.logoUrl && (
                        <Box sx={{ textAlign: "center", marginBottom: "24px" }}>
                            <img
                                src={form.logoUrl}
                                alt="Form Logo"
                                style={{ height: "80px", objectFit: "contain" }}
                            />
                        </Box>
                    )}

                    {/* Form Title */}
                    <Box sx={{ textAlign: "center", marginBottom: "24px" }}>
                        <Typography
                            sx={{
                                fontSize: "24px",
                                fontWeight: 600,
                                color: "#1E293B",
                                marginBottom: "8px",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {form.title}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            אנא מלא את הפרטים הבאים
                        </Typography>
                    </Box>

                    {/* Questions */}
                    {form.questions?.length === 0 ? (
                        <Box sx={{ textAlign: "center", padding: "48px 0" }}>
                            <Typography sx={{ color: "#64748B", fontFamily: '"Heebo", "Roboto", sans-serif' }}>
                                הטופס ריק. הוסף שאלות כדי לראות אותן כאן.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                            {form.questions?.map((question, index) => (
                                <Box key={question.id}>
                                    {renderQuestionPreview(question, index)}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Submit Button */}
                    {form.questions?.length > 0 && (
                        <Box sx={{ marginTop: "32px" }}>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{
                                    backgroundColor: "#57837E",
                                    padding: "12px",
                                    fontFamily: '"Heebo", "Roboto", sans-serif',
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    "&:hover": {
                                        backgroundColor: "#46706B"
                                    }
                                }}
                            >
                                שלח טופס
                            </Button>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default FormPreviewDialog;
